'use client';

import { useQuery } from '@tanstack/react-query';
import type React from 'react';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import { AdClickInputDto } from '@cineamo/api/models/advertisement/AdClickInputDto.types';
import { AdImpressionInputDto } from '@cineamo/api/models/advertisement/AdImpressionInputDto.types';
import { AdPlacement } from '@cineamo/api/models/advertisement/AdPlacement.enum';

import { AdvertisementPublicData, COOKIE_NAME_ADVERTISEMENT_CLOSED } from '@cineamo/api/helpers/advertisement-helper';

import { cookieStorage } from '@cineamo/ui/src/helpers/cookie-helper';

import { AdvertisementDisplayAdapter, AdvertisementTrackPayload } from './types';
import { useVisitorUid } from './useVisitorUid';

type AdvertisementContextType = {
    advertisement: AdvertisementPublicData | null | undefined;
    isVisible: Record<AdPlacement, boolean>;
    generateUid: () => string;
    onImpression: (data: AdImpressionInputDto | undefined, type: AdPlacement) => Promise<void>;
    onClick: (data: AdClickInputDto | undefined, type: AdPlacement) => Promise<void>;
    onClose: (type: AdPlacement) => void;
};

const AdvertisementContext = createContext<AdvertisementContextType | undefined>(undefined);

export const AdvertisementProvider: React.FC<{ adapter: AdvertisementDisplayAdapter; children: ReactNode }> = ({
    adapter,
    children
}) => {
    const {
        fetchAdvertisement,
        postImpression,
        postClick,
        displayLocation,
        cinemaId,
        referrerPage,
        ipAddress,
        enabled = true,
        onTrack
    } = adapter;

    // Stable per-visitor id — sent with the display request so the backend assigns the
    // same A/B variant to this visitor on every request.
    const { visitorId, generateUid } = useVisitorUid();

    const { data: advertisement } = useQuery({
        queryKey: ['advertisement', displayLocation, cinemaId, visitorId],
        queryFn: async () =>
            (await fetchAdvertisement({
                cinemaId,
                placements: [AdPlacement.LAYER, AdPlacement.TOP_BANNER],
                displayLocations: [displayLocation],
                visitorId: visitorId || undefined
            })) || null,
        enabled: enabled && visitorId !== undefined,
        // Sticky ad per mount: the variant assignment is sticky anyway, and a refocus refetch
        // swapping in a different ad mid-view would fire a second impression for the same view.
        staleTime: Infinity
    });

    const isLayerClosed = cookieStorage.getItem(
        COOKIE_NAME_ADVERTISEMENT_CLOSED(advertisement?.id ?? 0, AdPlacement.LAYER)
    );
    const isTopBannerClosed = cookieStorage.getItem(
        COOKIE_NAME_ADVERTISEMENT_CLOSED(advertisement?.id ?? 0, AdPlacement.TOP_BANNER)
    );

    const isVisible = useMemo(
        () =>
            ({
                [AdPlacement.LAYER]: advertisement?.placement?.includes(AdPlacement.LAYER) && !isLayerClosed,
                [AdPlacement.TOP_BANNER]:
                    advertisement?.placement?.includes(AdPlacement.TOP_BANNER) && !isTopBannerClosed
            }) as Record<AdPlacement, boolean>,
        [advertisement, isLayerClosed, isTopBannerClosed]
    );

    const trackingData: AdImpressionInputDto & AdClickInputDto = useMemo(
        () => ({
            client: displayLocation,
            cinema: cinemaId,
            ipAddress,
            // Attribute impressions/clicks to the served A/B variant.
            variant: advertisement?.variantId,
            referrerPage
        }),
        [displayLocation, cinemaId, ipAddress, advertisement?.variantId, referrerPage]
    );

    // What every `onTrack` event carries. Built here rather than in each host because only this
    // component holds the resolved advertisement: hosts receive the payload, not the ad. `contentId`
    // and `variantId` are what let Pulse attribute the event to a movie and to an A/B version.
    const buildTrackPayload = useCallback(
        (type: AdPlacement): AdvertisementTrackPayload => ({
            advertisementId: advertisement?.id ?? 0,
            placement: type,
            contentId: advertisement?.contentId,
            variantId: advertisement?.variantId
        }),
        [advertisement?.id, advertisement?.contentId, advertisement?.variantId]
    );

    const onClose = useCallback(
        (type: AdPlacement) => {
            onTrack?.('ad_close', buildTrackPayload(type));
            cookieStorage.setItem(COOKIE_NAME_ADVERTISEMENT_CLOSED(advertisement?.id ?? 0, type), true, {
                expires: new Date(advertisement?.endDatetime ?? ''),
                path: '/'
            });
        },
        [advertisement?.id, advertisement?.endDatetime, onTrack, buildTrackPayload]
    );

    const onClick = useCallback(
        async (data: AdClickInputDto = {}, type: AdPlacement) => {
            if (!advertisement?.id) {
                return;
            }
            onTrack?.('ad_click', buildTrackPayload(type));
            await postClick(advertisement?.id, { ...trackingData, ...data, placement: type });
        },
        [advertisement?.id, trackingData, onTrack, postClick, buildTrackPayload]
    );

    const onImpression = useCallback(
        async (data: AdImpressionInputDto = {}, type: AdPlacement) => {
            if (!advertisement?.id) {
                return;
            }
            onTrack?.('ad_impression', buildTrackPayload(type));
            await postImpression(advertisement?.id, { ...trackingData, ...data, placement: type });
        },
        [advertisement?.id, trackingData, onTrack, postImpression, buildTrackPayload]
    );

    const context = useMemo(
        () => ({
            advertisement,
            isVisible,
            generateUid,
            onImpression,
            onClick,
            onClose
        }),
        [advertisement, isVisible, generateUid, onImpression, onClick, onClose]
    );

    return <AdvertisementContext.Provider value={context}>{children}</AdvertisementContext.Provider>;
};

export const useAdvertisement = (): AdvertisementContextType => {
    const context = useContext(AdvertisementContext);
    if (context === undefined) {
        throw new Error('useAdvertisement must be used within a AdvertisementProvider');
    }
    return context;
};
